import { Icon } from "@iconify/react";
import { Breadcrumb, Button, Card, Col, Divider, Form, Input, Row, Select, message, Spin, Typography, Space } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ContentWrapper } from "../../../../../../Components/Layout/ContentWrapper";
import * as Yup from "yup";
import { Formik } from "formik";
import { api, panelApiBaseUrl } from "../../../../../../../SPA/Services/api.service";
import { panelApiUrl } from "~/Utils";
import detailStyles from "../../../../../WorkOrder/WorkOrder.module.scss";

export const AddVendor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const mode = searchParams.get("mode");
  const isEdit = !!id;
  const isView = mode === "view";

  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(isEdit);
  const [initialValues, setInitialValues] = useState({
    regionId: undefined,
    vendorName: "",
    vendorStatus: undefined as string | undefined,
    totalContracts: "",
    activeContracts: "",
  });
  const [regionData, setRegionData] = useState<any[]>([]);
  const [existingVendors, setExistingVendors] = useState<any[]>([]);

  useEffect(() => {
    fetchRegions();
    fetchVendors();
    if (isEdit) {
      fetchVendorDetails();
    }
  }, [id]);

  const fetchVendors = () => {
    api
      .get(`${panelApiBaseUrl}/vendor?page=0&size=1000`)
      .then((res: any) => {
        const records = res?.data?.response?.data?.records || res?.data?.data || [];
        setExistingVendors(records);
      })
      .catch((err) => {
        console.error("Error fetching vendors:", err);
      });
  };

  const fetchRegions = () => {
    api
      .get(`${panelApiBaseUrl}/region?page=0&size=1000`)
      .then((res: any) => {
        const responseData = res?.data?.response?.data?.records || res?.data?.data || [];
        const sortedData = [...responseData].sort((a: any, b: any) => (a.regionName || "").localeCompare(b.regionName || ""));
        setRegionData(sortedData);
      })
      .catch((err) => {
        console.error("Error fetching regions:", err);
      });
  };

  const fetchVendorDetails = () => {
    setIsDataLoading(true);
    api
      .get(`${panelApiBaseUrl}/vendor/${id}`)
      .then((res: any) => {
        const data = res?.data?.response?.data || res?.data?.data || {};
        setInitialValues({
          regionId: data?.regionId || data?.regionName,
          vendorName: data.vendorName || "",
          vendorStatus: data.isActive === false || data.active === false ? "Inactive" : "Active",
          totalContracts: data.totalContractCount?.toString() || "0",
          activeContracts: data.activeContractCount?.toString() || "0",
        });
      })
      .catch((err) => {
        message.error("Failed to fetch vendor details");
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  };

  const vendorSchema = Yup.object().shape({
    regionId: Yup.string().required("Please select a region"),
    vendorName: Yup.string()
      .required("Please enter vendor name")
      .trim("Leading and trailing spaces are not allowed")
      .strict(true)
      /* Unique WITHIN a region, not across all of them: the same vendor can
         serve East Coast and Northern. `this.parent` is the form's values, so
         the region being chosen is in scope. */
      .test("unique", "This name already exists under the selected region.", function (value) {
        if (!value) return true;
        const regionId = String((this.parent as any)?.regionId ?? "");
        if (!regionId) return true; // nothing to scope by yet
        const isDuplicate = existingVendors.some(
          (v: any) =>
            v.vendorName?.toLowerCase() === value.toLowerCase() &&
            String(v.regionId ?? v.region?.id ?? "") === regionId &&
            String(v.id) !== String(id)
        );
        return !isDuplicate;
      }),
    vendorStatus: Yup.string().required("Please select vendor status"),
  });

  if (isDataLoading) {
    return (
      <ContentWrapper title="">
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      </ContentWrapper>
    );
  }

  const header = (title: string) => (
    <Space align="start">
      <Icon
        onClick={() => navigate(-1)}
        icon="akar-icons:arrow-left"
        style={{
          fontSize: "1.8rem",
          cursor: "pointer",
        }}
      />
      <span>
        <Typography.Title
          level={3}
          style={{ margin: 0 }}
        >
          {title}
        </Typography.Title>
      </span>
    </Space>
  );

  /* View is a read-only summary, as the work order detail page draws one. */
  if (isView) {
    const fields = [
      ["Vendor Name", initialValues.vendorName],
      ["Status Of Vendor", initialValues.vendorStatus],
      ["Region", regionData.find((region) => region.id === initialValues.regionId)?.regionName ?? initialValues.regionId],
      ["Total Contracts", initialValues.totalContracts],
      ["Active Contracts", initialValues.activeContracts],
    ];
    return (
      <ContentWrapper customTitle={header("Vendor Details")}>
        <Typography.Title level={4}>Vendor Details</Typography.Title>
        <Row gutter={24}>
          {fields.map(([label, value]) => (
            <Col
              key={label}
              xs={24}
              sm={12}
              lg={8}
              className={detailStyles.detailGridRow}
            >
              <div className={detailStyles.detailLabel}>{label}</div>
              <div className={detailStyles.detailValue}>{value || "-"}</div>
            </Col>
          ))}
        </Row>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper customTitle={header(isEdit ? "Edit Vendor" : "Add Vendor")}>
      <Typography.Title level={4}>Vendor Details</Typography.Title>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={vendorSchema}
        validateOnMount
        onSubmit={(values, { resetForm }) => {
          setIsLoading(true);
          const payload = {
            regionId: values.regionId,
            regionName: regionData.find((region) => region.id === values.regionId)?.regionName,
            vendorName: values.vendorName,
            /* v2.1 vendors say isActive; older builds read active. */
            active: values.vendorStatus === "Active",
            isActive: values.vendorStatus === "Active",
          };

          if (isEdit) {
            api
              .put(`${panelApiBaseUrl}/vendor/${id}`, payload)
              .then((res: any) => {
                message.success(res?.data?.message || "Vendor updated successfully");
                navigate(-1);
              })
              .catch((err: any) => {
                message.error(err?.data?.message || "Failed to update vendor");
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            api
              .post(`${panelApiBaseUrl}/vendor`, payload)
              .then((res: any) => {
                message.success(res?.data?.message || "Vendor added successfully");
                navigate(-1);
              })
              .catch((err: any) => {
                message.error(err?.data?.message || "Failed to add vendor");
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        }}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, handleChange, handleBlur, handleSubmit, isValid, dirty }) => (
          <Form layout="vertical" onFinish={handleSubmit}>
            <div style={{ width: "70%" }}>
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Vendor Name"
                    validateStatus={touched.vendorName && errors.vendorName ? "error" : undefined}
                    help={touched.vendorName && typeof errors.vendorName === "string" ? errors.vendorName : undefined}
                  >
                    <Input
                      name="vendorName"
                      value={values.vendorName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Status of vendor"
                    validateStatus={touched.vendorStatus && errors.vendorStatus ? "error" : undefined}
                    help={touched.vendorStatus && typeof errors.vendorStatus === "string" ? errors.vendorStatus : undefined}
                  >
                    <Select
                      value={values.vendorStatus}
                      onChange={(value) => setFieldValue("vendorStatus", value)}
                      onBlur={() => setFieldTouched("vendorStatus", true)}
                      style={{ width: "100%" }}
                    >
                      <Select.Option value="Active">Active</Select.Option>
                      <Select.Option value="Inactive">Inactive</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Region"
                    validateStatus={touched.regionId && errors.regionId ? "error" : undefined}
                    help={touched.regionId && typeof errors.regionId === "string" ? errors.regionId : undefined}
                  >
                    <Select
                      value={values.regionId}
                      onChange={(value) => setFieldValue("regionId", value)}
                      onBlur={() => setFieldTouched("regionId", true)}
                      style={{ width: "100%" }}
                      showSearch
                      optionFilterProp="children"
                    >
                      {regionData.map((region) => (
                        <Select.Option key={region.id} value={region.id}>
                          {region.regionName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" gutter={16} style={{ marginTop: 32 }}>
                <Col>
                  <Button onClick={() => navigate(-1)} disabled={isLoading}>
                    Cancel
                  </Button>
                </Col>
                <Col>
                  <Button type="primary" htmlType="submit" disabled={!isValid} loading={isLoading}>
                    Submit
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </ContentWrapper>
  );
};
